import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useT } from '../i18n';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useRegistrations } from '../context/RegistrationsContext';
import { CATEGORIES, CAT_STYLE, getCategoriesForGender } from '../categories';

function openInCalendar(event) {
  const pad = (n) => String(n).padStart(2, '0');
  const toICSDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  };
  const uid = `sturmvogel-event-${event.id}@racing`;
  const now = new Date();
  const stamp = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}T${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}Z`;
  const dateStr = toICSDate(event.date);
  const times = (event.categories || []).map(c => c.start_time).filter(Boolean).sort();
  let dtstart, dtend;
  if (times.length > 0) {
    const [h, m] = times[0].split(':');
    dtstart = `${dateStr}T${pad(parseInt(h))}${pad(parseInt(m))}00`;
    dtend   = `${dateStr}T${pad(parseInt(h) + 4)}${pad(parseInt(m))}00`;
  } else {
    dtstart = dateStr; dtend = dateStr;
  }
  const desc = [
    event.description || '',
    event.location ? `Ort: ${event.location}` : '',
    event.url ? `Ausschreibung: ${event.url}` : '',
  ].filter(Boolean).join('\\n');

  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Sturmvogel Racing//DE',
    'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', 'BEGIN:VEVENT',
    `UID:${uid}`, `DTSTAMP:${stamp}`,
    times.length ? `DTSTART:${dtstart}` : `DTSTART;VALUE=DATE:${dtstart}`,
    times.length ? `DTEND:${dtend}`     : `DTEND;VALUE=DATE:${dtend}`,
    `SUMMARY:${event.name}`,
    event.location ? `LOCATION:${event.location}` : '',
    desc ? `DESCRIPTION:${desc}` : '',
    event.url ? `URL:${event.url}` : '',
    'END:VEVENT', 'END:VCALENDAR',
  ].filter(Boolean).join('\r\n');

  // data: URI with text/calendar MIME — no download attribute
  // → iOS Calendar / Android Calendar / macOS Calendar opens directly
  const dataUri = `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
  const a = document.createElement('a');
  a.href = dataUri;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function SpecRow({ label, value, highlight }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '13px 0', borderBottom: '1px solid rgba(59,73,75,0.2)', gap: 16 }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--outline)', flexShrink: 0, minWidth: 130 }}>
        {label}
      </span>
      <span style={{ fontFamily: highlight ? "'JetBrains Mono', monospace" : "var(--font-display)", fontSize: highlight ? 13 : 14, fontWeight: highlight ? 700 : 400, color: highlight ? 'var(--primary)' : (value ? 'var(--on-surface)' : 'var(--outline)'), textAlign: 'right', fontStyle: (!value && !highlight) ? 'italic' : 'normal', letterSpacing: highlight ? '0.05em' : 'normal', wordBreak: 'break-all', overflowWrap: 'anywhere', minWidth: 0 }}>
        {value || '—'}
      </span>
    </div>
  );
}

function DriverProfile({ token, driverId: myDriverId, role, isCoAdmin = false, onLogout, onLeaveTeam, onPhotoUpdate, onTokenRefresh }) {
  const t = useT();
  const navigate = useNavigate();
  const params = useParams();
  const isAdmin = role === 'admin';

  // Which driver to show: URL param if present, else own profile
  const targetId = params.id ? parseInt(params.id) : myDriverId;
  const isOwnProfile = !isAdmin && targetId === myDriverId;
  const canEdit = isAdmin || isOwnProfile;

  const { registrations, regsByDriver } = useRegistrations();

  const [driver, setDriver] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [coAdminLoading, setCoAdminLoading] = useState(false);
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const fileRef = useRef();
  const quickPhotoRef = useRef();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (targetId || isAdmin) loadDriver(); }, [targetId]);

  // Derive registered events from global context (stays in sync with matrix changes)
  const driverEvents = useMemo(() => {
    const myRegs = (regsByDriver[targetId] || []).filter(r => r.status === 'participating' || r.status === 'assigned');
    const myEventIds = new Set(myRegs.map(r => r.event_id));
    return allEvents
      .filter(e => myEventIds.has(e.id))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations, regsByDriver, allEvents, targetId]);

  const loadDriver = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [dRes, evRes] = await Promise.all([
        axios.get(`/api/drivers/${targetId}`, { headers }),
        axios.get('/api/events', { headers }).catch(() => ({ data: [] })),
      ]);
      const d = dRes.data;
      setDriver(d);
      setAllEvents(evRes.data || []);
      setPhotoPreview(d.photo_url || null);
      setFormData({
        name: d.name || '',
        license_number: d.license_number || '',
        uci_id: d.uci_id || '',
        email: d.email || '',
        phone: d.phone || '',
        dob: d.dob ? (d.dob.includes('T') ? d.dob.split('T')[0] : d.dob) : '',
        emergency_phone: d.emergency_phone || '',
        address: d.address || '',
        points: d.points ?? 0,
        category: d.category || 'Elite',
        gender: d.gender || '',
        verein: d.verein || '',
        photo_url: d.photo_url || '',
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      // Resize to max 400×400 before encoding
      const MAX = 400;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
      URL.revokeObjectURL(objectUrl);
      setPhotoPreview(dataUrl);
      setFormData((f) => ({ ...f, photo_url: dataUrl }));
    };
    img.src = objectUrl;
  };

  // Quick photo upload from view mode — no full form needed
  const handleQuickPhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = async () => {
      const MAX = 400;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
      URL.revokeObjectURL(objectUrl);
      setPhotoUploading(true);
      try {
        await axios.patch(`/api/drivers/${targetId}/photo`, { photo_url: dataUrl }, { headers: { Authorization: `Bearer ${token}` } });
        setPhotoPreview(dataUrl);
        setDriver((d) => ({ ...d, photo_url: dataUrl }));
        setFormData((f) => ({ ...f, photo_url: dataUrl }));
        if (isOwnProfile && onPhotoUpdate) onPhotoUpdate(dataUrl);
        setMessage('Profilbild aktualisiert ✓');
        setTimeout(() => setMessage(''), 3000);
      } catch (err) {
        setMessage('Fehler: ' + (err.response?.data?.error || err.message));
      } finally {
        setPhotoUploading(false);
      }
    };
    img.src = objectUrl;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/drivers/${targetId}`, formData, { headers: { Authorization: `Bearer ${token}` } });
      setMessage('Profil gespeichert ✓');
      setEditing(false);
      loadDriver();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCategoryChange = async (cat) => {
    try {
      await axios.put(`/api/drivers/${driver.id}`, { ...formData, category: cat }, { headers: { Authorization: `Bearer ${token}` } });
      loadDriver();
    } catch {}
  };

  const handleSetAccount = async () => {
    const email = window.prompt(`Login-E-Mail für ${driver.name}:`, driver.email || '');
    if (!email) return;
    const pw = window.prompt(`Passwort für ${driver.name} (min. 6 Zeichen):`);
    if (!pw || pw.length < 6) { alert('Passwort zu kurz.'); return; }
    try {
      await axios.post(`/api/drivers/${driver.id}/account`, { account_email: email, account_password: pw }, { headers: { Authorization: `Bearer ${token}` } });
      setMessage(`Login für ${driver.name} gesetzt ✓`);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRemoveDriver = async () => {
    setRemoveLoading(true);
    try {
      await axios.delete(`/api/drivers/${driver.id}`, { headers: { Authorization: `Bearer ${token}` } });
      navigate('/drivers');
    } catch (err) {
      setMessage('Fehler: ' + (err.response?.data?.error || err.message));
      setRemoveLoading(false);
      setShowRemoveConfirm(false);
    }
  };

  const handleCoAdminToggle = async () => {
    if (!driver) return;
    const newValue = !driver.is_co_admin;
    setCoAdminLoading(true);
    try {
      await axios.patch(`/api/drivers/${driver.id}/co-admin`, { is_co_admin: newValue }, { headers: { Authorization: `Bearer ${token}` } });
      setDriver(prev => ({ ...prev, is_co_admin: newValue }));
      setMessage(newValue ? `${driver.name} ist jetzt Co-Manager` : `Co-Manager Rechte von ${driver.name} entzogen`);
    } catch (err) {
      setMessage('Fehler: ' + (err.response?.data?.error || err.message));
    } finally {
      setCoAdminLoading(false);
    }
  };

  const handleLeaveTeam = async () => {
    setLeaveLoading(true);
    try {
      await axios.delete('/api/drivers/leave', { headers: { Authorization: `Bearer ${token}` } });
      if (onLeaveTeam) onLeaveTeam(); // update app state to "no team" mode — does NOT log out
    } catch (err) {
      setMessage('Fehler: ' + (err.response?.data?.error || err.message));
      setLeaveLoading(false);
      setShowLeaveConfirm(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteLoading(true);
    try {
      await axios.delete('/api/auth/driver-account', {
        headers: { Authorization: `Bearer ${token}` },
        data: { password: deletePassword },
      });
      if (onLogout) onLogout();
    } catch (err) {
      setMessage('Fehler: ' + (err.response?.data?.error || err.message));
      setDeleteLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setMessage(t('profile_pw_mismatch'));
      setTimeout(() => setMessage(''), 3500);
      return;
    }
    if (pwForm.newPassword.length < 6) {
      setMessage('Neues Passwort muss mindestens 6 Zeichen haben');
      setTimeout(() => setMessage(''), 3500);
      return;
    }
    setPwSaving(true);
    try {
      const res = await axios.put('/api/auth/change-password', {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.token && onTokenRefresh) onTokenRefresh(res.data.token);
      setMessage(`${t('profile_pw_changed')} ✓`);
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setMessage(''), 3500);
    } catch (err) {
      setMessage('Fehler: ' + (err.response?.data?.error || err.message));
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) return <div style={{ color: 'var(--outline)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, letterSpacing: '0.1em' }}>LADE PROFIL...</div>;

  if (!driver) return (
    <div>
      <div style={{ color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, marginBottom: 16 }}>Profil nicht gefunden.</div>
      <button className="btn btn-secondary" onClick={() => navigate('/drivers')}>← Zurück</button>
    </div>
  );

  const initials = driver.name ? driver.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '??';
  const cat = driver.category || 'Elite';
  const cs = CAT_STYLE[cat] || CAT_STYLE.Elite;

  return (
    <div>
      {/* Back */}
      <button onClick={() => navigate('/drivers')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 8, padding: 0 }}>
        ← Fahrerliste
      </button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: 4 }}>RIDER PROFILE</div>
          <h1 style={{ margin: 0 }}>{driver.name.toUpperCase()}</h1>
        </div>
        {canEdit && !editing && (
          <button className="btn btn-primary" onClick={() => setEditing(true)}>✎ BEARBEITEN</button>
        )}
      </div>

      {message && <div className={`alert ${message.includes('Fehler') ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: '1.5rem' }}>{message}</div>}

      {editing ? (
        /* ══════════════ EDIT FORM ══════════════ */
        <form onSubmit={handleSave}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>

            {/* Left: Photo + Identity */}
            <div className="glass-panel" style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: 16 }}>PROFILBILD</div>

              {/* Photo upload */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid rgba(59,73,75,0.2)' }}>
                <div
                  onClick={() => fileRef.current?.click()}
                  style={{ width: 80, height: 80, borderRadius: 4, background: photoPreview ? 'transparent' : 'rgba(var(--brand-primary-rgb),0.08)', border: `1px solid ${photoPreview ? 'rgba(var(--brand-primary-rgb),0.3)' : 'rgba(59,73,75,0.4)'}`, overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}
                >
                  {photoPreview
                    ? <img src={photoPreview} alt="Profil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontFamily: "var(--font-display)", fontSize: '1.5rem', fontWeight: 800, color: 'var(--primary)' }}>{initials}</span>
                  }
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
                  >
                    <span style={{ color: '#fff', fontSize: 20 }}>📷</span>
                  </div>
                </div>
                <div>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
                  <button type="button" className="btn btn-secondary" style={{ fontSize: 10, padding: '6px 12px', marginBottom: 6, display: 'block' }} onClick={() => fileRef.current?.click()}>Foto hochladen</button>
                  {photoPreview && <button type="button" className="btn btn-secondary" style={{ fontSize: 10, padding: '4px 12px' }} onClick={() => { setPhotoPreview(null); setFormData((f) => ({ ...f, photo_url: '' })); }}>Entfernen</button>}
                </div>
              </div>

              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: 12 }}>{t('profile_section_personal').toUpperCase()}</div>
              {[
                { label: t('profile_full_name'), key: 'name', type: 'text', required: true },
                { label: 'Geburtsdatum', key: 'dob', type: 'date' },
                { label: 'E-Mail', key: 'email', type: 'email' },
                { label: 'Telefon', key: 'phone', type: 'tel' },
                { label: 'Notfallnummer', key: 'emergency_phone', type: 'tel' },
                { label: 'Adresse', key: 'address', type: 'text' },
              ].map(({ label, key, type, required }) => (
                <div key={key} className="form-group">
                  <label>{label}</label>
                  <input type={type} value={formData[key] || ''} required={required} onChange={(e) => setFormData({ ...formData, [key]: e.target.value })} />
                </div>
              ))}
            </div>

            {/* Right: Race data */}
            <div className="glass-panel" style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: 12 }}>{t('profile_section_race').toUpperCase()}</div>
              {[
                { label: 'Lizenznummer', key: 'license_number', type: 'text' },
                { label: 'UCI-ID', key: 'uci_id', type: 'text', placeholder: 'z.B. GER19920101' },
                { label: 'Verein (für Punkte Sync)', key: 'verein', type: 'text', placeholder: 'z.B. RSC Sturmvogel' },
              ].map(({ label, key, type, placeholder }) => (
                <div key={key} className="form-group">
                  <label>{label}</label>
                  <input type={type} value={formData[key] || ''} placeholder={placeholder || ''} onChange={(e) => setFormData({ ...formData, [key]: e.target.value })} />
                </div>
              ))}

              {/* Points — editable by driver and admin */}
              {canEdit && (
                <div className="form-group">
                  <label>Punktestand</label>
                  <input type="number" min="0" value={formData.points ?? 0} onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })} />
                </div>
              )}

              {/* Gender — editable by driver and admin */}
              {canEdit && (
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label>Geschlecht</label>
                  <select
                    value={formData.gender || ''}
                    onChange={(e) => {
                      const g = e.target.value;
                      const cats = getCategoriesForGender(g || null);
                      const currentCatValid = cats.includes(formData.category);
                      setFormData({ ...formData, gender: g, category: currentCatValid ? formData.category : cats[0] });
                    }}
                    style={{ width: '100%', background: 'var(--surface)', border: 'none', borderBottom: '1px solid var(--outline-variant)', color: 'var(--on-surface)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, padding: '6px 0', outline: 'none' }}
                  >
                    <option value="">— Nicht angegeben</option>
                    <option value="männlich">Männlich</option>
                    <option value="weiblich">Weiblich</option>
                  </select>
                </div>
              )}

              {/* Category — editable by driver and admin */}
              {canEdit && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 10 }}>Rennklasse</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {getCategoriesForGender(formData.gender || null).map((c) => {
                      const s = CAT_STYLE[c] || CAT_STYLE.Elite;
                      const active = formData.category === c;
                      return (
                        <button key={c} type="button" onClick={() => setFormData({ ...formData, category: c })}
                          style={{ padding: '6px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', border: `1px solid ${active ? s.color : 'rgba(59,73,75,0.4)'}`, background: active ? s.bg : 'transparent', color: active ? s.color : 'var(--outline)', cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
                          {c.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── PASSWORT ÄNDERN (nur eigenes Profil) ── */}
          {isOwnProfile && (
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(59,73,75,0.2)' }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: '1rem' }}>{t('profile_change_pw').toUpperCase()}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 420 }}>
                {[['currentPassword', 'Aktuelles Passwort'], ['newPassword', 'Neues Passwort'], ['confirmPassword', 'Neues Passwort (Wdh.)']].map(([key, label]) => (
                  <div key={key}>
                    <label style={{ display: 'block', marginBottom: 7, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: '#849495' }}>{label}</label>
                    <input
                      type="password"
                      value={pwForm[key]}
                      onChange={e => setPwForm(f => ({ ...f, [key]: e.target.value }))}
                      style={{ width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(59,73,75,0.6)', color: '#e2e2e8', fontFamily: "var(--font-display)", fontSize: 14, padding: '6px 0', outline: 'none' }}
                      onFocus={e => e.target.style.borderBottomColor = 'var(--brand-primary)'}
                      onBlur={e => e.target.style.borderBottomColor = 'rgba(59,73,75,0.6)'}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handlePasswordChange}
                  disabled={pwSaving}
                  style={{ alignSelf: 'flex-start', padding: '9px 18px', background: 'transparent', border: '1px solid rgba(var(--brand-primary-rgb),0.4)', color: 'var(--brand-primary)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: pwSaving ? 'not-allowed' : 'pointer', opacity: pwSaving ? 0.6 : 1 }}
                >
                  {pwSaving ? '...' : t('profile_change_pw').toUpperCase()}
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: '1.5rem' }}>
            <button type="submit" className="btn btn-primary" style={{ boxShadow: '0 0 12px rgba(var(--brand-primary-rgb),0.3)' }}>SPEICHERN</button>
            <button type="button" className="btn btn-secondary" onClick={() => { setEditing(false); setPhotoPreview(driver.photo_url || null); }}>ABBRECHEN</button>
          </div>
        </form>
      ) : (
        /* ══════════════ VIEW MODE ══════════════ */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>

          {/* Left: Identity card */}
          <div className="glass-panel" style={{ padding: '2rem' }}>
            {/* Avatar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(59,73,75,0.25)' }}>
              {/* Avatar — clickable for canEdit users */}
              <div
                onClick={canEdit ? () => quickPhotoRef.current?.click() : undefined}
                style={{ width: 80, height: 80, borderRadius: 4, border: `1px solid ${cs.border}`, overflow: 'hidden', flexShrink: 0, background: cs.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: canEdit ? 'pointer' : 'default' }}
              >
                {photoUploading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--primary)', letterSpacing: '0.05em' }}>...</span>
                  </div>
                ) : (
                  <>
                    {driver.photo_url
                      ? <img src={driver.photo_url} alt={driver.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontFamily: "var(--font-display)", fontSize: '1.6rem', fontWeight: 800, color: cs.color }}>{initials}</span>
                    }
                    {canEdit && (
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
                      >
                        <span style={{ fontSize: 20 }}>📷</span>
                      </div>
                    )}
                  </>
                )}
              </div>
              {canEdit && (
                <input ref={quickPhotoRef} type="file" accept="image/*" onChange={handleQuickPhoto} style={{ display: 'none' }} />
              )}
              <div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => quickPhotoRef.current?.click()}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--primary)', textTransform: 'uppercase', marginBottom: 8, opacity: 0.75 }}
                  >
                    📷 {t('profile_photo').toUpperCase()}
                  </button>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: cs.color, background: cs.bg, border: `1px solid ${cs.border}`, padding: '2px 8px' }}>{cat.toUpperCase()}</span>
                  {isOwnProfile && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--primary)', background: 'rgba(var(--brand-primary-rgb),0.1)', border: '1px solid rgba(var(--brand-primary-rgb),0.2)', padding: '2px 8px' }}>{t('drivers_badge_me')}</span>}
                </div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: '1.25rem', fontWeight: 800, fontStyle: 'italic', color: 'var(--on-surface)', letterSpacing: '0.02em' }}>{driver.name}</div>
                {driver.uci_id && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--outline)', marginTop: 4 }}>UCI · {driver.uci_id}</div>}
              </div>
            </div>

            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: 4 }}>{t('profile_section_personal').toUpperCase()}</div>
            <SpecRow label={t('profile_dob')} value={driver.dob ? new Date(driver.dob).toLocaleDateString('de-DE') : null} />
            <SpecRow label={t('profile_gender')} value={driver.gender ? (driver.gender === 'männlich' ? `♂ ${t('drivers_form_gender_m')}` : `♀ ${t('drivers_form_gender_f')}`) : null} />
            <SpecRow label={t('profile_email')} value={driver.email} />
            <SpecRow label={t('profile_phone')} value={driver.phone} />
            <SpecRow label={t('profile_emergency')} value={driver.emergency_phone} />
            <SpecRow label={t('profile_address')} value={driver.address} />

            {/* Race data — below personal data on the left */}
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(59,73,75,0.2)' }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: 4 }}>{t('profile_section_race').toUpperCase()}</div>
              {driver.public_id && (
                <SpecRow label={t('drivers_col_id')} value={driver.public_id} highlight />
              )}
              <SpecRow label={t('profile_license')} value={driver.license_number} highlight />
              <SpecRow label={t('profile_uci')} value={driver.uci_id} highlight />
              <SpecRow label="Verein" value={driver.verein} />

              {/* Rennklasse */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 0', borderBottom: '1px solid rgba(59,73,75,0.2)' }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--outline)', flexShrink: 0, minWidth: 130 }}>{t('profile_class')}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: cs.color, background: cs.bg, border: `1px solid ${cs.border}`, padding: '3px 9px', borderRadius: 99 }}>
                  {cat.toUpperCase()}
                </span>
              </div>

              {/* Admin: change category */}
              {isAdmin && (
                <div style={{ paddingTop: '0.75rem', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {CATEGORIES.map((c) => {
                    const s = CAT_STYLE[c];
                    const active = cat === c;
                    return (
                      <button key={c} onClick={() => handleCategoryChange(c)}
                        style={{ padding: '4px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', border: `1px solid ${active ? s.color : 'rgba(59,73,75,0.4)'}`, background: active ? s.bg : 'transparent', color: active ? s.color : 'var(--outline)', cursor: 'pointer', borderRadius: 99, whiteSpace: 'nowrap', transition: 'all 0.12s' }}>
                        {c.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Points + Rang */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid rgba(59,73,75,0.2)', marginTop: isAdmin ? '0.75rem' : 0 }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--outline)' }}>Punktestand</span>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  {driver.radnet_rang && (
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: 'var(--outline)', letterSpacing: '0.05em' }}>
                      #{driver.radnet_rang}
                    </span>
                  )}
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 28, fontWeight: 700, color: 'var(--primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>{driver.points ?? 0}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--outline)' }}>PTS</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Registered Events + Race Eligibility */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* ── Registered Events ── */}
            <div className="glass-panel" style={{ padding: '2rem' }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: '1rem' }}>
                ANGEMELDETE RENNEN
                <span style={{ marginLeft: 10, background: 'rgba(var(--brand-primary-rgb),0.12)', color: 'var(--primary)', border: '1px solid rgba(var(--brand-primary-rgb),0.25)', padding: '1px 7px', fontSize: 9 }}>
                  {driverEvents.length}
                </span>
              </div>

              {driverEvents.length === 0 ? (
                <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'var(--outline)', fontStyle: 'italic' }}>
                  Keine Anmeldungen vorhanden.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {driverEvents.map((ev) => {
                    const today = new Date(); today.setHours(0,0,0,0);
                    const evDate = new Date(ev.date);
                    const isPast = evDate < today;
                    const fmtDate = evDate.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
                    const isMandatory = ev.is_mandatory;
                    return (
                      <div
                        key={ev.id}
                        onClick={() => navigate(`/events/${ev.id}`)}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px', background: isPast ? 'rgba(26,28,32,0.3)' : isMandatory ? 'rgba(255,140,0,0.06)' : 'rgba(var(--brand-primary-rgb),0.05)', border: `1px solid ${isPast ? 'rgba(59,73,75,0.2)' : isMandatory ? 'rgba(255,160,50,0.35)' : 'rgba(var(--brand-primary-rgb),0.2)'}`, opacity: isPast ? 0.6 : 1, cursor: 'pointer', transition: 'background 0.12s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = isPast ? 'rgba(59,73,75,0.15)' : isMandatory ? 'rgba(255,140,0,0.12)' : 'rgba(var(--brand-primary-rgb),0.1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isPast ? 'rgba(26,28,32,0.3)' : isMandatory ? 'rgba(255,140,0,0.06)' : 'rgba(var(--brand-primary-rgb),0.05)'; }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: isMandatory ? 2 : 0 }}>
                            {isMandatory && !isPast && (
                              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', background: '#ff9a1f', color: '#1a0800', padding: '1px 5px', flexShrink: 0 }}>PFLICHT</span>
                            )}
                          </div>
                          <div style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 700, fontStyle: 'italic', color: isPast ? 'var(--on-surface-variant)' : 'var(--on-surface)', letterSpacing: '0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {ev.name}
                          </div>
                          <div style={{ display: 'flex', gap: 10, marginTop: 3, alignItems: 'center' }}>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: isPast ? 'var(--outline)' : isMandatory ? '#ff9a1f' : 'var(--primary)', fontWeight: 700 }}>{fmtDate}</span>
                            {ev.location && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)' }}>· {ev.location}</span>}
                            {isPast && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'var(--outline)', background: 'rgba(59,73,75,0.2)', padding: '1px 5px' }}>VERGANGEN</span>}
                          </div>
                        </div>
                        <button
                          onClick={e => { e.stopPropagation(); openInCalendar(ev); }}
                          title="In Kalender speichern"
                          style={{ flexShrink: 0, background: 'transparent', border: '1px solid rgba(59,73,75,0.4)', color: 'var(--outline)', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', transition: 'all 0.15s' }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(var(--brand-primary-rgb),0.4)'; e.currentTarget.style.color = 'var(--primary)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(59,73,75,0.4)'; e.currentTarget.style.color = 'var(--outline)'; }}
                        >
                          📅 .ICS
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ── ADMIN ACTIONS ── */}
      {isAdmin && !editing && (
        <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'rgba(26,28,32,0.35)', border: '1px solid rgba(59,73,75,0.25)' }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: 16 }}>ADMIN</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <button
              onClick={handleSetAccount}
              style={{ padding: '9px 18px', background: 'transparent', border: '1px solid rgba(var(--brand-primary-rgb),0.35)', color: 'var(--primary)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', transition: 'all 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              🔑 LOGIN BEARBEITEN
            </button>

            {/* Co-Admin Toggle — nur original Team-Admin, nicht für Co-Admins */}
            {isAdmin && driver?.team_id && (
              <button
                onClick={handleCoAdminToggle}
                disabled={coAdminLoading}
                style={{ padding: '9px 18px', background: driver.is_co_admin ? 'rgba(255,154,31,0.12)' : 'transparent', border: `1px solid ${driver.is_co_admin ? 'rgba(255,154,31,0.5)' : 'rgba(255,154,31,0.3)'}`, color: driver.is_co_admin ? '#ff9a1f' : 'rgba(255,154,31,0.6)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: coAdminLoading ? 'not-allowed' : 'pointer', opacity: coAdminLoading ? 0.6 : 1, transition: 'all 0.15s' }}
                onMouseEnter={e => { if (!coAdminLoading) { e.currentTarget.style.background = 'rgba(255,154,31,0.12)'; e.currentTarget.style.borderColor = 'rgba(255,154,31,0.6)'; e.currentTarget.style.color = '#ff9a1f'; } }}
                onMouseLeave={e => { if (!driver.is_co_admin) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,154,31,0.3)'; e.currentTarget.style.color = 'rgba(255,154,31,0.6)'; } }}
              >
                {coAdminLoading ? '...' : driver.is_co_admin ? '★ CO-MANAGER ENTZIEHEN' : '☆ ZUM CO-MANAGER ERNENNEN'}
              </button>
            )}

            {!showRemoveConfirm ? (
              <button
                onClick={() => setShowRemoveConfirm(true)}
                style={{ padding: '9px 18px', background: 'transparent', border: '1px solid rgba(255,180,171,0.3)', color: 'rgba(255,180,171,0.7)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,180,171,0.7)'; e.currentTarget.style.color = '#ffb4ab'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,180,171,0.3)'; e.currentTarget.style.color = 'rgba(255,180,171,0.7)'; }}
              >
                ✕ AUS TEAM ENTFERNEN
              </button>
            ) : (
              <div style={{ background: 'rgba(255,180,171,0.04)', border: '1px solid rgba(255,180,171,0.2)', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'rgba(185,202,203,0.8)' }}>
                  {driver.name} wirklich aus dem Team entfernen?
                </span>
                <button
                  disabled={removeLoading}
                  onClick={handleRemoveDriver}
                  style={{ padding: '7px 16px', background: 'rgba(255,180,171,0.15)', border: '1px solid rgba(255,180,171,0.5)', color: '#ffb4ab', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: removeLoading ? 'not-allowed' : 'pointer', opacity: removeLoading ? 0.6 : 1 }}
                >
                  {removeLoading ? '...' : 'ENTFERNEN'}
                </button>
                <button
                  onClick={() => setShowRemoveConfirm(false)}
                  style={{ padding: '7px 14px', background: 'transparent', border: '1px solid var(--outline-variant)', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' }}
                >
                  ABBRECHEN
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ACCOUNT LÖSCHEN — nur eigenes Profil, nur Fahrer ── */}
      {isOwnProfile && (
        <div style={{ marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,180,171,0.12)' }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--error, #ffb4ab)', marginBottom: 16 }}>DANGER ZONE</div>

          {/* Team verlassen */}
          {driver.team_id && (
            <div style={{ marginBottom: 16 }}>
              {!showLeaveConfirm ? (
                <button
                  onClick={() => setShowLeaveConfirm(true)}
                  style={{ background: 'transparent', border: '1px solid rgba(255,180,171,0.3)', color: 'rgba(255,180,171,0.6)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', padding: '9px 18px', cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,180,171,0.7)'; e.currentTarget.style.color = '#ffb4ab'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,180,171,0.3)'; e.currentTarget.style.color = 'rgba(255,180,171,0.6)'; }}
                >
                  ← TEAM VERLASSEN
                </button>
              ) : (
                <div style={{ background: 'rgba(255,180,171,0.04)', border: '1px solid rgba(255,180,171,0.2)', padding: '1.25rem', maxWidth: 420 }}>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'rgba(185,202,203,0.8)', lineHeight: 1.6, marginBottom: 16 }}>
                    Du verlässt das Team und wirst aus dem Roster entfernt. Dein Account bleibt bestehen — du kannst danach einem neuen Team beitreten.
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      disabled={leaveLoading}
                      onClick={handleLeaveTeam}
                      style={{ padding: '9px 18px', background: 'rgba(255,180,171,0.15)', border: '1px solid rgba(255,180,171,0.5)', color: '#ffb4ab', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: leaveLoading ? 'not-allowed' : 'pointer', opacity: leaveLoading ? 0.6 : 1 }}
                    >
                      {leaveLoading ? '...' : 'TEAM VERLASSEN'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowLeaveConfirm(false)}
                      style={{ padding: '9px 14px', background: 'transparent', border: '1px solid var(--outline-variant)', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' }}
                    >
                      ABBRECHEN
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{ background: 'transparent', border: '1px solid rgba(255,180,171,0.3)', color: 'rgba(255,180,171,0.6)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', padding: '9px 18px', cursor: 'pointer', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,180,171,0.7)'; e.currentTarget.style.color = '#ffb4ab'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,180,171,0.3)'; e.currentTarget.style.color = 'rgba(255,180,171,0.6)'; }}
            >
              ✕ {t('profile_delete_account').toUpperCase()}
            </button>
          ) : (
            <div style={{ background: 'rgba(255,180,171,0.04)', border: '1px solid rgba(255,180,171,0.2)', padding: '1.25rem', maxWidth: 420 }}>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'rgba(185,202,203,0.8)', lineHeight: 1.6, marginBottom: 16 }}>
                Dein Account wird <strong style={{ color: '#ffb4ab' }}>dauerhaft gelöscht</strong> — inklusive aller Profildaten. Diese Aktion kann nicht rückgängig gemacht werden.
              </div>
              <form onSubmit={handleDeleteAccount}>
                <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', color: 'rgba(255,180,171,0.7)', marginBottom: 6 }}>{t('profile_pw_confirm').toUpperCase()}</label>
                <input
                  type="password"
                  required
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                  placeholder="••••••••"
                  style={{ width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,180,171,0.35)', color: '#e2e2e8', fontFamily: "var(--font-display)", fontSize: 14, padding: '6px 0', outline: 'none', marginBottom: 16 }}
                  onFocus={e => e.target.style.borderBottomColor = '#ffb4ab'}
                  onBlur={e  => e.target.style.borderBottomColor = 'rgba(255,180,171,0.35)'}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" disabled={deleteLoading} style={{ padding: '9px 18px', background: 'rgba(255,180,171,0.15)', border: '1px solid rgba(255,180,171,0.5)', color: '#ffb4ab', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: deleteLoading ? 'not-allowed' : 'pointer', opacity: deleteLoading ? 0.6 : 1 }}>
                    {deleteLoading ? '...' : t('profile_delete_confirm').toUpperCase()}
                  </button>
                  <button type="button" onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); }} style={{ padding: '9px 14px', background: 'transparent', border: '1px solid var(--outline-variant)', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' }}>
                    ABBRECHEN
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* ── MOBILE: Einstellungen + Logout (nur eigenes Profil) ── */}
      {isOwnProfile && (
        <div className="dp-mobile-actions">
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: 12 }}>KONTO</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              onClick={() => navigate('/settings')}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', background: 'rgba(26,28,32,0.6)', border: '1px solid rgba(59,73,75,0.35)', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', textAlign: 'left', width: '100%' }}
            >
              <span style={{ fontSize: 16 }}>⚙</span> EINSTELLUNGEN
            </button>
            <button
              onClick={onLogout}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', background: 'rgba(26,28,32,0.6)', border: '1px solid rgba(59,73,75,0.35)', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', textAlign: 'left', width: '100%' }}
            >
              <span style={{ fontSize: 16 }}>⏻</span> ABMELDEN
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DriverProfile;
